<script setup lang="ts">
import { mayModerate, rankOf, rolesOf } from '../lib/roles'
import { timeLeft } from '../lib/format'
import { computed, onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { SpaceInfo, SpaceMember } from '../api/types'
import { describeError } from '../api/http'
import { useSession } from '../stores/session'
import Avatar from './Avatar.vue'
import SafetyNumberModal from './SafetyNumberModal.vue'

const props = defineProps<{ space: SpaceInfo }>()
const session = useSession()
const router = useRouter()
const open = ref<string | null>(null)
const verifying = ref<string | null>(null)

// Online first; then the owner, then by role, highest first.
const isOnline = (m: SpaceMember) => Number(session.online.has(m.profile.user_id))
const members = computed(() =>
  [...props.space.members].sort(
    (a, b) =>
      isOnline(b) - isOnline(a) ||
      rankOf(props.space, a) - rankOf(props.space, b) ||
      a.profile.display_name.localeCompare(b.profile.display_name),
  ),
)
const onlineCount = computed(() => props.space.members.filter(isOnline).length)

const canKick = (m: SpaceMember) => !!session.me && mayModerate(props.space, session.me, m.profile.user_id, 'remove_members')

async function message(m: SpaceMember): Promise<void> {
  open.value = null
  try {
    const id = await session.createDm([m.profile.user_id])
    await router.push({ name: 'dm', params: { convId: id } })
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

// Suspensions (HRTH-95): no messages, calls or parties here for a while.
const now = ref(Date.now())
const tick = setInterval(() => (now.value = Date.now()), 30_000)
onBeforeUnmount(() => clearInterval(tick))
const suspended = (m: SpaceMember) => (m.suspended_until ?? 0) > now.value
const mayDo = (m: SpaceMember, perm: 'suspend_members' | 'lift_suspensions') =>
  !!session.me && mayModerate(props.space, session.me, m.profile.user_id, perm)
const LENGTHS = [
  { label: '10 min', minutes: 10 },
  { label: '1 hour', minutes: 60 },
  { label: '1 day', minutes: 24 * 60 },
  { label: '1 week', minutes: 7 * 24 * 60 },
  { label: '30 days', minutes: 30 * 24 * 60 },
]
const choosing = ref<string | null>(null)
const customHours = ref(2)

async function suspend(m: SpaceMember, minutes: number): Promise<void> {
  open.value = null
  choosing.value = null
  try {
    await session.suspendMember(props.space.id, m.profile.user_id, Math.round(minutes))
    session.toast(`${m.profile.display_name} is suspended here for ${timeLeft(Date.now() + minutes * 60_000)}`)
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function lift(m: SpaceMember): Promise<void> {
  open.value = null
  try {
    await session.liftSuspension(props.space.id, m.profile.user_id)
    session.toast(`${m.profile.display_name}'s suspension is lifted`)
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function kick(m: SpaceMember): Promise<void> {
  open.value = null
  if (!confirm(`Remove ${m.profile.display_name} from ${props.space.name}?`)) return
  try {
    await session.kick(props.space.id, m.profile.user_id)
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}
</script>

<template>
  <aside class="scroll-thin w-60 shrink-0 overflow-y-auto border-l border-border bg-bg-secondary py-4" aria-label="Members" data-testid="space-members">
    <h3 class="label px-4">
      Members / {{ String(space.members.length).padStart(2, '0') }}<span class="text-text-disabled"> · {{ onlineCount }} online</span>
    </h3>
    <div v-for="m in members" :key="m.profile.user_id" class="relative">
      <button
        class="flex w-full items-center gap-2.5 px-4 py-1.5 text-left hover:bg-bg-hover"
        :data-testid="`member-${m.profile.display_name}`"
        @click="open = open === m.profile.user_id ? null : m.profile.user_id"
      >
        <Avatar :id="m.profile.user_id" :name="m.profile.display_name" :size="24" presence />
        <span class="min-w-0 flex-1 truncate text-sm text-text-secondary">{{ m.profile.display_name }}</span>
        <span v-if="m.role !== 'member'" class="mono border border-border px-1 text-[9px] tracking-[0.1em] text-text-muted uppercase">{{ m.role }}</span>
        <span
          v-if="suspended(m)"
          class="mono border border-signal px-1 text-[9px] tracking-[0.1em] text-signal uppercase"
          :title="`Suspended until ${new Date(m.suspended_until!).toLocaleString()}`"
          :data-testid="`member-suspended-${m.profile.display_name}`"
        >{{ timeLeft(m.suspended_until!, now) }}</span>
      </button>
      <div v-if="rolesOf(space, m).length" class="-mt-1 flex flex-wrap gap-x-2 gap-y-0.5 pr-4 pb-1 pl-[3.25rem]" :data-testid="`member-roles-${m.profile.display_name}`">
        <span v-for="r in rolesOf(space, m)" :key="r.id" class="mono flex items-center gap-1 text-[10px] text-text-muted">
          <span class="h-1.5 w-1.5" :style="{ background: r.color }" />{{ r.name }}
        </span>
      </div>
      <div
        v-if="open === m.profile.user_id"
        class="panel absolute right-2 left-2 z-20 mt-1 border-border-strong py-1"
      >
        <div class="mono px-3 py-1 break-all text-text-muted">{{ m.profile.user_id }}</div>
        <button
          v-if="m.profile.user_id !== session.me"
          class="btn-ghost w-full justify-start border-0"
          @click="message(m)"
        >
          Message
        </button>
        <button
          v-if="m.profile.user_id !== session.me"
          class="btn-ghost w-full justify-start border-0"
          data-testid="verify-member"
          @click="(verifying = m.profile.user_id), (open = null)"
        >
          Verify safety number
        </button>
        <template v-if="!suspended(m) && mayDo(m, 'suspend_members')">
          <button
            v-if="choosing !== m.profile.user_id"
            class="btn-ghost w-full justify-start border-0"
            data-testid="member-suspend"
            @click="choosing = m.profile.user_id"
          >
            Suspend…
          </button>
          <div v-else class="px-3 py-1" data-testid="member-suspend-lengths">
            <div class="label mb-1">Suspend for</div>
            <div class="flex flex-wrap gap-1">
              <button
                v-for="l in LENGTHS"
                :key="l.minutes"
                class="btn-outline h-7 px-2"
                :data-testid="`suspend-${l.minutes}`"
                @click="suspend(m, l.minutes)"
              >
                {{ l.label }}
              </button>
            </div>
            <form class="mt-1.5 flex items-center gap-1" @submit.prevent="suspend(m, customHours * 60)">
              <input v-model.number="customHours" type="number" min="1" max="8760" class="input h-7 w-16 py-0 text-xs" aria-label="Hours" />
              <span class="mono text-text-muted">hours</span>
              <button class="btn-outline h-7 px-2" :disabled="!(customHours >= 1 && customHours <= 8760)">Suspend</button>
            </form>
          </div>
        </template>
        <button
          v-if="suspended(m) && mayDo(m, 'lift_suspensions')"
          class="btn-ghost w-full justify-start border-0"
          data-testid="member-lift"
          @click="lift(m)"
        >
          Lift suspension
        </button>
        <button v-if="canKick(m)" class="btn-ghost w-full justify-start border-0 text-error hover:text-error" @click="kick(m)">
          Remove from space
        </button>
      </div>
    </div>
    <SafetyNumberModal v-if="verifying" :user-id="verifying" @close="verifying = null" />
  </aside>
</template>
